import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import "./styles/index.css";

import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";

import Login from "./pages/auth/Login";
import Setup from "./pages/auth/Setup";
import UserValidation from "./components/UserValidation";
import Dashboard from "./pages/dashboard/Dashboard";
import UsersList from "./pages/users/UsersList";
import AdminsList from "./pages/admins/AdminsList";
import RolesList from "./pages/roles/RolesList";
import AuditLogsList from "./pages/audit-logs/AuditLogsList";
import Maintenance from "./pages/system/Maintenance";
import HelpSupport from "./pages/system/HelpSupport";
import AdminEmailPage from "./pages/system/AdminEmailPage";
import AppVersionPage from "./pages/system/AppVersionPage";
import OtpFallbackPage from "./pages/system/OtpFallbackPage";
import AppCheckAlertPage from "./pages/system/AppCheckAlertPage";
import PhoneRegionsPage from "./pages/system/PhoneRegionsPage";
import BlockedEmailsPage from "./pages/system/BlockedEmailsPage";
import Exports from "./pages/exports/Exports";
import Forbidden from "./pages/common/Forbidden";
import ListingsPage from "./pages/listings/ListingsPage";
import ListingsManagePage from "./pages/listings/ListingsManagePage";
import CompaniesPage from "./pages/companies/CompaniesPage";
import FormConfigList from "./pages/form-config/FormConfigList";
import FormConfigEditor from "./pages/form-config/FormConfigEditor";
import TopUpPackagesPage from "./pages/topup-packages/TopUpPackagesPage";
import UserPointHistoryPage from "./pages/point-history/UserPointHistoryPage";
import ListingCategoriesPage from "./pages/listing-categories/ListingCategoriesPage";
import FormPricingPage from "./pages/form-config/FormPricingPage";
import CvReviewPage from "./pages/cv-review/CvReviewPage";
import AiSettingsPage from "./pages/ai/AiSettingsPage";
import AiLogsPage from "./pages/ai/AiLogsPage";
import PromotionPeriodsPage from "./pages/promotions/PromotionPeriodsPage";
import PromoCodeEventsPage from "./pages/promotions/PromoCodeEventsPage";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import BookingsPage from "./pages/bookings/BookingsPage";
import PaymentTransactionsPage from "./pages/payment-transactions/PaymentTransactionsPage";
import CharterDestinationsPage from "./pages/charter-destinations/CharterDestinationsPage";

import PermissionRoute from "./components/PermissionRoute";
import { Permission } from "./permissions/permission.enum";
import AdminNotifications from "./pages/notifications/AdminNotifications";
import UserNotifications from "./pages/notifications/UserNotifications";
import NotificationSender from "./pages/notifications/NotificationSender";
import SavedSearchTestPush from "./pages/notifications/SavedSearchTestPush";
import { TEST_TOOLS_ENABLED } from "./config/api.config";
import SentNotifications from "./pages/notifications/SentNotifications";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useTranslation("common");

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        {t("loading")}
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <UserValidation />
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-200">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/setup" element={<Setup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/forbidden" element={<Forbidden />} />

                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/users"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.USERS_READ}>
                        <UsersList />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admins"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.ADMINS_READ}>
                        <AdminsList />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/roles"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.ROLES_READ}>
                        <RolesList />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/audit-logs"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.AUDIT_LOGS_READ}>
                        <AuditLogsList />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/maintenance"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute
                        permission={Permission.SYSTEM_SETTINGS_READ}
                      >
                        <Maintenance />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/system/help-support"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <HelpSupport />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/system/admin-email"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <AdminEmailPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/system/app-version"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <AppVersionPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/system/app-check-alert"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <AppCheckAlertPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/system/otp-fallback"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <OtpFallbackPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/system/phone-regions"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <PhoneRegionsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/system/blocked-emails"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <BlockedEmailsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/exports"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.USERS_READ}>
                        <Exports />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/notifications/send"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute
                        permission={Permission.USER_NOTIFICATIONS_CREATE}
                      >
                        <NotificationSender />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                {TEST_TOOLS_ENABLED && (
                  <Route
                    path="/notifications/test-push"
                    element={
                      <ProtectedRoute>
                        <PermissionRoute
                          permission={Permission.USER_NOTIFICATIONS_CREATE}
                        >
                          <SavedSearchTestPush />
                        </PermissionRoute>
                      </ProtectedRoute>
                    }
                  />
                )}

                <Route
                  path="/notifications/sent"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute
                        permission={Permission.ADMIN_NOTIFICATIONS_READ}
                      >
                        <SentNotifications />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/notifications/admin"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute
                        permission={Permission.ADMIN_NOTIFICATIONS_READ}
                      >
                        <AdminNotifications />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/notifications/user"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute
                        permission={Permission.USER_NOTIFICATIONS_READ}
                      >
                        <UserNotifications />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/listings"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.LISTINGS_READ}>
                        <ListingsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/listings/manage"
                  element={
                    <ProtectedRoute>
                      {/* users.read, not listings.read — GET /admin/listings/manage
                          enforces users.read, like every marketplace-admin route. */}
                      <PermissionRoute permission={Permission.USERS_READ}>
                        <ListingsManagePage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/bookings"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.BOOKINGS_READ}>
                        <BookingsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/cv-review"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.USERS_VERIFICATION}>
                        <CvReviewPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/companies"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.LISTINGS_READ}>
                        <CompaniesPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/form-config"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <FormConfigList />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/form-config/pricing"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.FINANCE_READ}>
                        <FormPricingPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/form-config/:formType"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_UPDATE}>
                        <FormConfigEditor />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/points/top-up-packages"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.FINANCE_READ}>
                        <TopUpPackagesPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/promotions/periods"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.FINANCE_READ}>
                        <PromotionPeriodsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/promotions/codes"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.FINANCE_READ}>
                        <PromoCodeEventsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/payment-transactions"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.FINANCE_READ}>
                        <PaymentTransactionsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/users/:userId/points"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.POINTS_HISTORY_READ}>
                        <UserPointHistoryPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/listing-categories"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <ListingCategoriesPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ai/settings"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <AiSettingsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ai/logs"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.SYSTEM_SETTINGS_READ}>
                        <AiLogsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/charter/destinations"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.CHARTER_DESTINATIONS_READ}>
                        <CharterDestinationsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
